import { useState, useCallback, useRef, useEffect } from 'react';
// @ts-ignore - Cesium module resolution issue
import * as Cesium from 'cesium';
import { calculateTerrainAwareAltitude } from '../utils/areaUtils';
import { demoProjectsService } from '../services/demoProjectsService';
import { projectService } from '../services/projectService';
import { mapLayerStatuses } from '../services/projects/structure/structureService';
import { useAuth } from '../contexts/AuthContext';
import { useUserProjectsContext } from '../contexts/UserProjectsContext';
// Order-independent fingerprint of a layer-status set, used to skip redundant
// tree rebuilds while still catching state / retry changes.
const layerStatusSignature = (list) => list
    .map(l => `${l.layerName}:${l.state}:${l.userRetryCount}:${l.retryAvailable ? 1 : 0}`)
    .sort()
    .join('|');
// Helper functions for artifact categorization and display
const getArtifactTypeName = (type) => {
    switch (type) {
        case 1: return 'Ground Overlay';
        case 2: return 'Vector Data';
        case 3: return 'Screen Overlay';
        case 4: return 'Placemark';
        case 5: return 'Info Panel';
        default: return 'Unknown';
    }
};
const getArtifactTypeColor = (type) => {
    switch (type) {
        case 1: return 'bg-orange-600'; // Ground Overlay
        case 2: return 'bg-green-600'; // Vector Data
        case 3: return 'bg-blue-600'; // Screen Overlay
        case 4: return 'bg-purple-600'; // Placemark
        case 5: return 'bg-yellow-600'; // Info Panel
        default: return 'bg-gray-600';
    }
};
export const useProjects = (viewer, loadedArtifacts, clearAllArtifactsCallback, addStatus) => {
    const { isAuthenticated } = useAuth();
    const { projects: userProjects } = useUserProjectsContext();
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [folders, setFolders] = useState([]);
    const [artifacts, setArtifacts] = useState([]);
    const [folderTree, setFolderTree] = useState([]);
    const [isLoadingProjectStructure, setIsLoadingProjectStructure] = useState(false);
    const viewerRef = useRef(viewer);
    const projectsLoadedRef = useRef(false);
    const retryCountRef = useRef(0);
    // Signature of the last applied layer-status set, so silent refreshes can
    // detect status-only changes (processing→failed, retry count/availability)
    // that leave folder/artifact counts unchanged.
    const lastLayerStatusSignatureRef = useRef('');
    const maxRetries = 5;
    // Removed internal polling; polling is managed by ProjectStructureSection for user projects
    // Update refs when values change
    useEffect(() => {
        viewerRef.current = viewer;
    }, [viewer]);
    // Build folder tree structure - optimized with useCallback
    const buildFolderTree = useCallback((folders, artifacts, layerStatuses = []) => {
        const folderMap = new Map();
        const nodeMap = new Map();
        // Create folder map and initial nodes
        folders.forEach(folder => {
            folderMap.set(folder.getId(), folder);
            nodeMap.set(folder.getId(), {
                folder,
                children: [],
                artifacts: [],
                layerStatuses: []
            });
        });
        // Assign artifacts to folders
        artifacts.forEach(artifact => {
            const folderId = artifact.getFolderId();
            const node = nodeMap.get(folderId);
            if (node) {
                node.artifacts.push(artifact);
            }
        });
        // Attach in-progress/failed layer statuses to each folder they produce into.
        // A status can reference more than one folder (e.g. flow accumulation + TWI).
        layerStatuses.forEach(ls => {
            ls.folderIds.forEach(folderId => {
                const node = nodeMap.get(folderId);
                if (node) {
                    node.layerStatuses.push(ls);
                }
            });
        });
        // Sort artifacts within each folder by sort_order
        nodeMap.forEach(node => {
            node.artifacts.sort((a, b) => {
                var _a, _b;
                const sortOrderA = (_a = a.getSortOrder()) !== null && _a !== void 0 ? _a : 9999;
                const sortOrderB = (_b = b.getSortOrder()) !== null && _b !== void 0 ? _b : 9999;
                return sortOrderA - sortOrderB;
            });
        });
        // Canonical folder order. The backend returns folders ORDER BY sort_order
        // ASC, so their position in this array IS their intended display order. Use
        // that to order folders rather than the minimum artifact sortOrder —
        // otherwise a folder with no artifacts yet (e.g. a still-processing layer's
        // placeholder) falls to the bottom instead of sitting in its real spot.
        const folderOrder = new Map();
        folders.forEach((folder, index) => folderOrder.set(folder.getId(), index));
        const folderRank = (node) => { var _a; return (_a = folderOrder.get(node.folder.getId())) !== null && _a !== void 0 ? _a : Number.MAX_SAFE_INTEGER; };
        // Build tree structure
        const rootNodes = [];
        folders.forEach(folder => {
            const node = nodeMap.get(folder.getId());
            const parentId = folder.getParentId();
            if (!parentId || parentId === '') {
                // Root folder
                rootNodes.push(node);
            }
            else {
                // Child folder
                const parentNode = nodeMap.get(parentId);
                if (parentNode) {
                    parentNode.children.push(node);
                }
            }
        });
        // Sort folders by canonical backend order (keeps placeholder folders in place)
        rootNodes.sort((a, b) => folderRank(a) - folderRank(b));
        nodeMap.forEach(node => {
            node.children.sort((a, b) => folderRank(a) - folderRank(b));
        });
        // Prune folders that have no artifacts in their descendant tree — but keep
        // any folder that has an in-progress or failed layer, so the user can see
        // (and retry) work that hasn't produced artifacts yet.
        const prune = (node) => {
            var _a, _b;
            const prunedChildren = node.children
                .map(child => prune(child))
                .filter((child) => child !== null);
            const hasArtifactsHere = node.artifacts.length > 0;
            const hasArtifactsInDescendants = prunedChildren.length > 0;
            const hasActiveStatusHere = ((_b = (_a = node.layerStatuses) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0;
            if (!hasArtifactsHere && !hasArtifactsInDescendants && !hasActiveStatusHere) {
                return null;
            }
            return { ...node, children: prunedChildren };
        };
        const prunedRoots = rootNodes
            .map(root => prune(root))
            .filter((n) => n !== null);
        return prunedRoots;
    }, []);
    const loadProjects = useCallback(async () => {
        // Projects are now loaded by useUserProjects for user projects and DemoProjectsSection for demo projects
        // This hook now only manages the selected project state and project structure loading
        addStatus('📋 Projects managed by dedicated hooks - ready for project selection');
        projectsLoadedRef.current = true;
    }, [addStatus]);
    // Manual retry function for when automatic retries are exhausted
    const retryLoadProjects = useCallback(() => {
        retryCountRef.current = 0; // Reset retry count
        projectsLoadedRef.current = false; // Allow loading again
        loadProjects();
    }, [loadProjects]);
    // Auto-load projects on startup
    useEffect(() => {
        if (viewer && !projectsLoadedRef.current) {
            loadProjects();
        }
    }, [viewer, loadProjects]);
    const loadProjectStructure = useCallback(async (projectId) => {
        const currentViewer = viewerRef.current;
        if (!currentViewer)
            return;
        // Only show loader when there is no data currently displayed.
        const hasExistingData = folders.length > 0 || artifacts.length > 0;
        setIsLoadingProjectStructure(!hasExistingData);
        addStatus(`🔄 Loading project structure for ${projectId}...`);
        try {
            let response;
            let layerStatusesList = [];
            // Determine if this is a user project by checking if the project ID is in the user projects list
            const isUserProject = isAuthenticated && userProjects.some(up => up.id === projectId);
            if (isUserProject) {
                // For user's own projects, use UserProjectsService via projectService
                const result = await projectService.getUserProjectStructure(projectId);
                response = {
                    getFoldersList: () => result.folders,
                    getArtifactsList: () => result.artifacts
                };
                layerStatusesList = result.layerStatuses || [];
            }
            else {
                // For demo projects (whether signed-in or not), use demo projects service
                response = await demoProjectsService.getProjectStructure(projectId);
                if (typeof response.getLayerStatusesList === 'function') {
                    layerStatusesList = mapLayerStatuses(response.getLayerStatusesList());
                }
            }
            const foldersList = response.getFoldersList() || [];
            const artifactsList = response.getArtifactsList() || [];
            setFolders(foldersList);
            setArtifacts(artifactsList);
            // Build folder tree
            const tree = buildFolderTree(foldersList, artifactsList, layerStatusesList);
            setFolderTree(tree);
            lastLayerStatusSignatureRef.current = layerStatusSignature(layerStatusesList);
            // Clear loading state
            setIsLoadingProjectStructure(false);
            addStatus(`✅ Loaded project structure: ${foldersList.length} folders, ${artifactsList.length} artifacts`);
            // Show artifact type breakdown
            const typeCount = new Map();
            artifactsList.forEach((artifact) => {
                const type = artifact.getType();
                const typeName = getArtifactTypeName(type);
                typeCount.set(typeName, (typeCount.get(typeName) || 0) + 1);
            });
            const typeBreakdown = Array.from(typeCount.entries())
                .map(([name, count]) => `${name}: ${count}`)
                .join(', ');
            addStatus(`📊 Artifacts: ${typeBreakdown}`);
            // Advisory for demo projects
            addStatus('🗂️ Click "Load" to add artifacts to the map');
        }
        catch (err) {
            addStatus(`❌ Error loading project structure: ${err.code || err.message}`);
            setIsLoadingProjectStructure(false);
        }
    }, [addStatus, buildFolderTree, folders, artifacts, isAuthenticated, userProjects]);
    // Background/Invisible refresh: do not flip loading state or clear UI
    const refreshProjectStructureSilently = useCallback(async (projectId) => {
        try {
            let response;
            let layerStatusesList = [];
            // Determine if this is a user project by checking if the project ID is in the user projects list
            const isUserProject = isAuthenticated && userProjects.some(up => up.id === projectId);
            if (isUserProject) {
                // For user's own projects, use UserProjectsService via projectService
                const result = await projectService.getUserProjectStructure(projectId);
                response = {
                    getFoldersList: () => result.folders,
                    getArtifactsList: () => result.artifacts
                };
                layerStatusesList = result.layerStatuses || [];
            }
            else {
                // For demo projects (whether signed-in or not), use demo projects service
                response = await demoProjectsService.getProjectStructure(projectId);
                if (typeof response.getLayerStatusesList === 'function') {
                    layerStatusesList = mapLayerStatuses(response.getLayerStatusesList());
                }
            }
            const foldersList = response.getFoldersList() || [];
            const artifactsList = response.getArtifactsList() || [];
            // If response is empty but we currently have data, ignore to avoid UI flicker
            const currentlyHasData = folders.length > 0 || artifacts.length > 0;
            const responseIsEmpty = foldersList.length === 0 && artifactsList.length === 0;
            if (currentlyHasData && responseIsEmpty) {
                return;
            }
            // If nothing changed, avoid state updates to prevent re-renders. Layer
            // statuses are part of "changed" — a processing→failed flip or a retry
            // (which changes count / availability) must rebuild the tree even when
            // folder/artifact counts are identical.
            const sameFolders = foldersList.length === folders.length;
            const sameArtifacts = artifactsList.length === artifacts.length;
            const sameLayerStatuses = layerStatusSignature(layerStatusesList) === lastLayerStatusSignatureRef.current;
            if (!sameFolders || !sameArtifacts || !sameLayerStatuses) {
                lastLayerStatusSignatureRef.current = layerStatusSignature(layerStatusesList);
                setFolders(foldersList);
                setArtifacts(artifactsList);
                const tree = buildFolderTree(foldersList, artifactsList, layerStatusesList);
                setFolderTree(tree);
            }
        }
        catch (err) {
            // Silent failure; do not surface loader
            return;
        }
    }, [folders, artifacts, buildFolderTree, isAuthenticated, userProjects]);
    const clearAllArtifacts = useCallback(() => {
        const currentViewer = viewerRef.current;
        if (!currentViewer)
            return;
        // Use the provided callback to clear artifacts
        clearAllArtifactsCallback();
        setFolders([]);
        setArtifacts([]);
        setFolderTree([]);
    }, [clearAllArtifactsCallback]);
    // No internal polling cleanup needed
    const selectProject = useCallback(async (project) => {
        const currentViewer = viewerRef.current;
        const projectId = project.getId();
        const isCurrentlySelected = (selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId()) === projectId;
        setSelectedProject(project);
        addStatus(`📁 Selected project: ${project.getName()}`);
        // Clear any existing user project polygons/labels when switching to demo projects
        if (currentViewer) {
            currentViewer.entities.removeById('user-project-polygon');
            currentViewer.entities.removeById('user-project-label');
        }
        // Clear previous project structure when switching projects
        if (!isCurrentlySelected) {
            // Also drop any loaded map layers from the previous view. Their presigned
            // URLs were captured at load time and may now be dead — e.g. a demo that
            // got bumped to low-res has had its full-res outputs/ deleted, so a stale
            // layer would render broken until a hard refresh. Starting each project
            // with a clean layer slate means the fresh structure (loaded below) serves
            // the correct (low-res) URLs on next load. clearAll only removes loaded
            // server layers — drawings, comments and scope items are untouched.
            clearAllArtifactsCallback();
            setFolders([]);
            setArtifacts([]);
            setFolderTree([]);
        }
        // Always load project structure for demo projects (since they come through events)
        // For authenticated users, only load if it's a different project
        if (!isCurrentlySelected || !isAuthenticated) {
            // Load project structure
            loadProjectStructure(projectId);
        }
        // Always fly to project bounds if available (for navigation/refocus)
        const bounds = project.getBounds();
        if (bounds && currentViewer) {
            const west = bounds.getWest();
            const south = bounds.getSouth();
            const east = bounds.getEast();
            const north = bounds.getNorth();
            try {
                // Calculate terrain-aware altitude for project bounds
                const boundsRect = { west, south, east, north };
                const terrainAwareAltitude = await calculateTerrainAwareAltitude(currentViewer, boundsRect);
                // Create destination position above terrain
                const centerLng = (west + east) / 2;
                const centerLat = (south + north) / 2;
                const destination = Cesium.Cartesian3.fromDegrees(centerLng, centerLat, terrainAwareAltitude);
                currentViewer.camera.flyTo({
                    destination,
                    duration: 2.0,
                    complete: () => {
                        addStatus(`🛩️ Flying to project bounds`);
                    }
                });
            }
            catch (error) {
                console.warn('Failed to calculate terrain-aware altitude, falling back to rectangle flyTo:', error);
                // Fallback to original rectangle-based flyTo
                const rectangle = Cesium.Rectangle.fromDegrees(west, south, east, north);
                currentViewer.camera.flyTo({
                    destination: rectangle,
                    duration: 2.0,
                    complete: () => {
                        addStatus(`🛩️ Flying to project bounds`);
                    }
                });
            }
        }
    }, [addStatus, loadProjectStructure, selectedProject, clearAllArtifactsCallback]);
    const flyToData = useCallback(() => {
        const currentViewer = viewerRef.current;
        if (!currentViewer || loadedArtifacts.size === 0)
            return;
        try {
            // Calculate bounds from all loaded artifacts
            let west = 180, south = 90, east = -180, north = -90;
            let hasBounds = false;
            loadedArtifacts.forEach((artifact) => {
                try {
                    const metadataJson = JSON.parse(artifact.metadata.getMetadata());
                    const bounds = metadataJson.bounds;
                    if (bounds) {
                        west = Math.min(west, bounds.west);
                        south = Math.min(south, bounds.south);
                        east = Math.max(east, bounds.east);
                        north = Math.max(north, bounds.north);
                        hasBounds = true;
                    }
                }
                catch (e) {
                    // Skip artifacts without bounds
                }
            });
            if (hasBounds) {
                const rectangle = Cesium.Rectangle.fromDegrees(west, south, east, north);
                currentViewer.camera.flyTo({
                    destination: rectangle,
                    duration: 2.0
                });
                addStatus('🛩️ Flying to loaded artifact bounds');
            }
            else {
                currentViewer.flyTo(currentViewer.entities);
                addStatus('🛩️ Flying to data (entities)');
            }
        }
        catch (error) {
            addStatus('❌ Error flying to data');
        }
    }, [loadedArtifacts, addStatus]);
    const resetView = useCallback(() => {
        const currentViewer = viewerRef.current;
        if (!currentViewer)
            return;
        currentViewer.camera.setView({
            destination: Cesium.Cartesian3.fromDegrees(-122.4194, 37.7749, 1000000),
            orientation: {
                heading: Cesium.Math.toRadians(0.0),
                pitch: Cesium.Math.toRadians(-45.0),
                roll: 0.0
            }
        });
        addStatus('🔄 Reset view');
    }, [addStatus]);
    return {
        projects,
        selectedProject,
        folders,
        artifacts,
        folderTree,
        isLoadingProjectStructure,
        loadProjects,
        retryLoadProjects,
        selectProject,
        loadProjectStructure,
        refreshProjectStructureSilently,
        clearAllArtifacts,
        flyToData,
        resetView,
        getArtifactTypeName,
        getArtifactTypeColor,
        // Expose setters for user project structure updates
        setFolders,
        setArtifacts,
        setFolderTree,
        setSelectedProject,
        buildFolderTree
    };
};
